/**
 * Display labels for a traveller in the checkout UI.
 * Used to correctly show "Primary traveller" and "Guest" tags per acceptance criteria.
 */
export interface TravellerDisplayLabels {
  /** Show the "Primary traveller" tag */
  isPrimaryTraveller: boolean
  /** Show the "Guest" tag - only for actual guests, never for employees */
  showGuestTag: boolean
}

/**
 * Computes the correct display labels for a traveller in a multi-room LEBT booking.
 *
 * Acceptance criteria:
 * - There can only be one Primary Traveller per room
 * - Guests are only Primary Traveller if they are the only traveller in the room
 * - A guest who is primary (only traveller in room) gets both 'Guest' and 'Primary Traveller' tags
 * - An employee should never get the 'Guest' tag, even if not primary
 * - If employees share a room, the first in employeeIds is primary
 * - Primary is first in [...employeeIds, ...guestIds] (employees before guests)
 *
 * @param occupancy - Room occupancy with employeeIds and guestIds
 * @param travellerForm - The traveller form (has employeeId or guestId, type)
 * @returns Display labels for the traveller
 */
export function getTravellerDisplayLabels(
  occupancy: App.Occupants | App.RoomOccupants | undefined,
  travellerForm: App.Checkout.TravellerForm | undefined,
): TravellerDisplayLabels {
  const isEmployee = travellerForm?.type === 'employee'
  const isGuest = travellerForm?.type === 'guest'

  if (!occupancy || (!isEmployee && !isGuest)) {
    return { isPrimaryTraveller: false, showGuestTag: false }
  }

  const roomTravellerIds = [
    ...(occupancy.employeeIds ?? []),
    ...(occupancy.guestIds ?? []),
  ]

  const travellerId = travellerForm?.employeeId ?? travellerForm?.guestId
  if (!travellerId || !roomTravellerIds.includes(travellerId)) {
    return { isPrimaryTraveller: false, showGuestTag: isGuest }
  }

  const primaryTravellerId = roomTravellerIds[0]
  const isPrimaryTraveller = travellerId === primaryTravellerId

  return {
    isPrimaryTraveller,
    showGuestTag: isGuest,
  }
}

/**
 * Sorts traveller form IDs so they match the room's occupancy order
 * (primary traveller first). Travellers are ordered by their position
 * in [...employeeIds, ...guestIds]. Any traveller not found in the
 * occupancy is placed at the end.
 *
 * @param travellerIds - Array of traveller form IDs (e.g. "adult-1", "adult-3")
 * @param occupancy - Room occupancy with employeeIds and guestIds
 * @param formDataById - Map of form ID to traveller form (to resolve employeeId/guestId)
 * @returns A new sorted array of traveller form IDs
 */
export function sortTravellerIdsByOccupancy(
  travellerIds: Array<string>,
  occupancy: App.Occupants | App.RoomOccupants,
  formDataById: Record<string, App.Checkout.TravellerForm | undefined>,
): Array<string> {
  const occupancyOrder = [
    ...(occupancy.employeeIds ?? []),
    ...(occupancy.guestIds ?? []),
  ]

  return [...travellerIds].sort((a, b) => {
    const formA = formDataById[a]
    const formB = formDataById[b]
    const idA = formA?.employeeId ?? formA?.guestId
    const idB = formB?.employeeId ?? formB?.guestId
    const indexA = idA ? occupancyOrder.indexOf(idA) : -1
    const indexB = idB ? occupancyOrder.indexOf(idB) : -1
    return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB)
  })
}

/**
 * Reorders `ids` so that any IDs present in `originalOrder` come first
 * (in their original relative order), followed by new IDs not in `originalOrder`.
 * Used to preserve the existing primary traveller when rooms are reassigned.
 *
 * @param ids - The current set of IDs to reorder
 * @param originalOrder - The previous ordering to preserve
 * @returns A new array with retained IDs first, then newly added IDs
 */
export function stableReorder(ids: Array<string>, originalOrder: Array<string>): Array<string> {
  const idsSet = new Set(ids)
  const originalSet = new Set(originalOrder)
  const retained = originalOrder.filter(id => idsSet.has(id))
  const added = ids.filter(id => !originalSet.has(id))
  return [...retained, ...added]
}

/**
 * Helper to check if a room should be included in business credits calculation.
 *
 * Returns true if:
 * - Room allocation has NOT been done yet (no employeeIds AND no guestIds) - assume employees will use the room
 * - Room has at least one employee assigned
 *
 * Returns false if:
 * - Room allocation has been done AND room has ONLY guest travellers (guestIds present but no employeeIds)
 *
 * Note on "pending allocation" state:
 * When neither employeeIds nor guestIds are present, we optimistically assume employees will use the room.
 * This is safe because:
 * 1. This function is called from memoized Redux selectors (e.g., selectToBeCalculatedCheckoutBusinessOfferCreditItems)
 * 2. When occupancy changes in Redux state, the selector chain automatically recalculates
 * 3. If allocation completes with only guests, the selectors will recompute and credits will update accordingly
 */
export function roomHasEmployeeTravellers(itemView: App.Checkout.AccommodationItemView): boolean {
  const occupancy = itemView.item.occupancy
  if (!occupancy) {
    // No occupancy data - include in credits (default behavior for pending allocation)
    return true
  }

  const employeeIds = occupancy.employeeIds
  const guestIds = occupancy.guestIds

  const hasEmployees = Array.isArray(employeeIds) && employeeIds.some(id => id && id.trim().length > 0)
  const hasGuests = Array.isArray(guestIds) && guestIds.length > 0

  // If room allocation hasn't been done (no employees AND no guests assigned), include the room.
  // This is an optimistic default - assume employees will use the room until allocation is complete.
  // Recalculation happens automatically via Redux selector dependencies when occupancy state changes.
  if (!hasEmployees && !hasGuests) {
    return true
  }

  return hasEmployees
}
